export const normalizeAdminApproveAction = (action) =>
  action === "exit" ? "exit" : "next";

export const normalizeAdminVisualEditorApproveAction =
  normalizeAdminApproveAction;

export const appendAdminContentRefreshQuery = (path, marker = Date.now()) => {
  const normalizedPath = String(path || "").trim();
  if (!normalizedPath) return normalizedPath;

  const [pathWithoutHash, hash = ""] = normalizedPath.split("#", 2);
  const [basePath, queryString = ""] = pathWithoutHash.split("?", 2);
  const query = new URLSearchParams(queryString);
  query.set("refreshContentAt", String(marker));
  const nextQuery = query.toString();
  return [
    basePath,
    nextQuery ? `?${nextQuery}` : "",
    hash ? `#${hash}` : "",
  ].join("");
};

const readValue = (value) => {
  if (typeof value === "function") return value();
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value;
};

export const getAdminApproveDecision = ({
  action = "next",
  approveInFlight = false,
  isApproving = false,
  canApprove = false,
  isComplete = false,
  canAdvanceNext = false,
} = {}) => {
  const normalizedAction = normalizeAdminApproveAction(action);

  if (approveInFlight || isApproving || !canApprove || !isComplete) {
    return { action: normalizedAction, shouldApprove: false };
  }

  if (normalizedAction === "next" && !canAdvanceNext) {
    return { action: normalizedAction, shouldApprove: false };
  }

  return { action: normalizedAction, shouldApprove: true };
};

export const getAdminVisualEditorApproveDecision = getAdminApproveDecision;

export const runAdminApproveAction = async ({
  action = "next",
  approveInFlight,
  isApproving,
  canApprove,
  isComplete,
  canAdvanceNext,
  beforeApprove = async () => {},
  approve,
  completeApprovedDraftAction,
} = {}) => {
  const decision = getAdminApproveDecision({
    action,
    approveInFlight: readValue(approveInFlight),
    isApproving: readValue(isApproving),
    canApprove: readValue(canApprove),
    isComplete: readValue(isComplete),
    canAdvanceNext: readValue(canAdvanceNext),
  });

  if (!decision.shouldApprove) return false;

  if (approveInFlight && typeof approveInFlight === "object") {
    approveInFlight.value = true;
  }

  try {
    await beforeApprove();
    const approved = await approve();
    if (approved) {
      await completeApprovedDraftAction(decision.action);
    }
    return Boolean(approved);
  } finally {
    if (approveInFlight && typeof approveInFlight === "object") {
      approveInFlight.value = false;
    }
  }
};

export const runAdminVisualEditorApproveAction = runAdminApproveAction;

export const runAdminVisualEditorApproveActionWithRecovery = async ({
  approvalCompleted,
  getRecoveryPath = () => "",
  onNavigationError,
  ...options
} = {}) => {
  if (readValue(approvalCompleted)) {
    return { status: "blocked", approved: false };
  }

  let approvalSucceeded = false;

  try {
    const approved = await runAdminApproveAction({
      ...options,
      approve: async () => {
        const result = await options.approve();
        approvalSucceeded = Boolean(result);
        if (
          approvalSucceeded &&
          approvalCompleted &&
          typeof approvalCompleted === "object"
        ) {
          approvalCompleted.value = true;
        }
        return result;
      },
    });

    return {
      status: approved ? "approved" : "blocked",
      approved: Boolean(approved),
    };
  } catch (error) {
    if (!approvalSucceeded) throw error;

    let recoveryPath = "";
    try {
      recoveryPath = getRecoveryPath();
    } catch {
      // The caller can still present a safe manual recovery action.
    }
    const result = {
      status: "approved_navigation_failed",
      approved: true,
      error,
      recoveryPath,
    };
    await callIfFunction(onNavigationError, result);
    return result;
  }
};

const normalizeAdminDraftPreviewApproveAction = (action) =>
  action === "close" || action === "exit" ? "close" : "next";

const callIfFunction = async (callback, value) => {
  if (typeof callback === "function") {
    await callback(value);
  }
};

export const completeAdminDraftPreviewApproval = async ({
  action = "next",
  queue,
  fallbackPath = "",
  closePreview = async () => false,
  navigateToExit = async () => true,
} = {}) => {
  const normalizedAction = normalizeAdminDraftPreviewApproveAction(action);

  if (normalizedAction === "next") {
    const advanced = await queue.removeCurrentAndAdvance();
    if (advanced === false) {
      throw new Error("Next draft navigation failed after approval.");
    }
    return { action: normalizedAction, advanced: true };
  }

  queue.consumeCurrent();
  const closed = Boolean(await closePreview());
  if (closed) {
    return { action: normalizedAction, exitPath: "", closed: true };
  }

  const exitPath = queue.getExitPath(fallbackPath);
  const navigated = await navigateToExit(exitPath);
  if (navigated === false) {
    throw new Error(
      "Safe draft preview exit navigation failed after approval.",
    );
  }

  return { action: normalizedAction, exitPath, closed: false };
};

export const runAdminDraftPreviewApproveAction = async ({
  action = "next",
  approveInFlight,
  approvalCompleted,
  isApproving,
  canApprove,
  isComplete,
  canApproveNext,
  approve,
  completeApprovedDraftAction,
  getExitPath = () => "",
  onSuccess,
  onApprovalError,
  onCompletionError,
} = {}) => {
  const normalizedAction = normalizeAdminDraftPreviewApproveAction(action);
  if (readValue(approvalCompleted)) {
    return {
      status: "blocked",
      approved: false,
      action: normalizedAction,
    };
  }

  let approvalAttempted = false;
  let approvalSucceeded = false;

  try {
    const approved = await runAdminApproveAction({
      action: normalizedAction === "close" ? "exit" : "next",
      approveInFlight,
      isApproving,
      canApprove,
      isComplete,
      canAdvanceNext: canApproveNext,
      approve: async () => {
        approvalAttempted = true;
        const approvalResult = await approve();
        approvalSucceeded = Boolean(approvalResult);
        if (
          approvalSucceeded &&
          approvalCompleted &&
          typeof approvalCompleted === "object"
        ) {
          approvalCompleted.value = true;
        }
        return approvalResult;
      },
      completeApprovedDraftAction: async () => {
        await completeApprovedDraftAction(normalizedAction);
      },
    });

    if (!approved) {
      if (!approvalAttempted) {
        return {
          status: "blocked",
          approved: false,
          action: normalizedAction,
        };
      }

      const result = {
        status: "approval_failed",
        approved: false,
        action: normalizedAction,
        error: null,
      };
      await callIfFunction(onApprovalError, null);
      return result;
    }

    const result = {
      status: "approved",
      approved: true,
      action: normalizedAction,
    };
    await callIfFunction(onSuccess, result);
    return result;
  } catch (error) {
    if (!approvalSucceeded) {
      const result = {
        status: "approval_failed",
        approved: false,
        action: normalizedAction,
        error,
      };
      await callIfFunction(onApprovalError, error);
      return result;
    }

    let exitPath = "";
    try {
      exitPath = getExitPath();
    } catch {
      // Recovery remains available to callers with their own fallback path.
    }
    const result = {
      status: "approved_completion_failed",
      approved: true,
      action: normalizedAction,
      error,
      exitPath,
    };
    await callIfFunction(onCompletionError, result);
    return result;
  }
};
